'use client';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCurrentUser, useDispatch } from '@witmetrics/data-store';
import { API } from '@/api';
import { useAppState } from '@/providers/AppStateProvider';
import { usePath } from '@/hooks/usePath';
import { useToggle } from '@/hooks/useToggle';
import Frame from '@/components/Frame';
import Panel from '@/components/Panel';
import Logo from '@/components/Logo';
import PlatformsSelector from './PlatformSelector';
import AccountSelector, { type AccountSelectorProps } from './AccountSelector';
import LoginForm from './LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import ResetPasswordForm from './ResetPasswordForm';
import ForgotPasswordConfirmation from './ForgotPasswordConfirmation';
import ResetPasswordConfirmation from './ResetPasswordConfirmation';
import WelcomeMessage from './WelcomeMessage';
import type { LoginView } from './types';
import { VIEWS } from './constants';
import { classes, fetchSessionUser } from './utils';

export default function Page() {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const currentUser = useCurrentUser();
  const { searchParams, router } = usePath();
  const [currentView, setCurrentView] = useState<LoginView>(VIEWS.LOGIN);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [isCheckingSession, toggleCheckingSession] = useToggle(false);
  const [isResettingPassword, toggleResettingPassword] = useToggle(false);

  useEffect(() => {
    if (searchParams.get('token')) {
      setResetToken(searchParams.get('token'));
      setCurrentView(VIEWS.RESET_PASSWORD);
    } else {
      checkSession();
    }
  }, []);

  const checkSession = async () => {
    try {
      toggleCheckingSession(true);
      const user = await fetchSessionUser();
      dispatch.addCurrentUser(user);
      toggleCheckingSession(false);
    } catch (err) {
      toggleCheckingSession(false);
      onApiError(err, 'Error checking user session');
    }
  };

  const handleForgotPassword = async (email: string) => {
    try {
      toggleResettingPassword(true);
      await API.Users.requestPasswordReset(email);
      toggleResettingPassword(false);
      setCurrentView(VIEWS.FORGOT_PASSWORD_CONFIRMATION);
    } catch (err) {
      toggleResettingPassword(false);
      onApiError(err, 'Error resetting password');
    }
  };

  const handleResetPassword = async (password: string) => {
    try {
      toggleResettingPassword(true);
      await API.Users.resetPassword(password, resetToken!);
      toggleResettingPassword(false);
      setCurrentView(VIEWS.RESET_PASSWORD_CONFIRMATION);
    } catch (err) {
      toggleResettingPassword(false);
      onApiError(err, 'Error resetting password');
    }
  };

  const handleReturnToLogin = () => {
    setCurrentView(VIEWS.LOGIN);
  };

  const handleLogout = async () => {
    try {
      await API.Users.logout();
      dispatch.resetStore();
      setCurrentView(VIEWS.LOGIN);
    } catch (err) {
      onApiError(err, 'Error logging out');
    }
  };

  const handleSelectAccount: AccountSelectorProps['onSelectAccount'] = async (
    accountType,
    accountId
  ) => {
    try {
      await API.Users.setActiveAccount(accountType, accountId);
      const redirect = searchParams.get('redirect');
      if (redirect) router.push(redirect);
      else setCurrentView(VIEWS.SELECT_PLATFORM);
    } catch (err) {
      onApiError(err, 'Error setting active account');
    }
  };

  const renderAuthView = () => {
    if (currentView === VIEWS.LOGIN) {
      return (
        <LoginForm
          onForgotPassword={() => setCurrentView(VIEWS.FORGOT_PASSWORD)}
        />
      );
    } else if (currentView === VIEWS.FORGOT_PASSWORD) {
      return (
        <ForgotPasswordForm
          isResettingPassword={isResettingPassword}
          onReturnToLogin={handleReturnToLogin}
          onSubmit={handleForgotPassword}
        />
      );
    } else if (currentView === VIEWS.RESET_PASSWORD) {
      return (
        <ResetPasswordForm
          isResettingPassword={isResettingPassword}
          onReturnToLogin={handleReturnToLogin}
          onSubmit={handleResetPassword}
        />
      );
    } else if (currentView === VIEWS.FORGOT_PASSWORD_CONFIRMATION) {
      return (
        <ForgotPasswordConfirmation onReturnToLogin={handleReturnToLogin} />
      );
    } else if (currentView === VIEWS.RESET_PASSWORD_CONFIRMATION) {
      return (
        <ResetPasswordConfirmation onReturnToLogin={handleReturnToLogin} />
      );
    }
  };

  const renderRightColumn = () => {
    if (!currentUser) return null;
    return currentView === VIEWS.SELECT_PLATFORM ? (
      <PlatformsSelector onBack={() => setCurrentView(VIEWS.LOGIN)} />
    ) : (
      <AccountSelector onSelectAccount={handleSelectAccount} />
    );
  };

  if (currentUser) {
    return (
      <div className={classes.wrapper}>
        <Frame shape="split">
          <Panel side="left">
            <WelcomeMessage
              message={`Please select ${currentView === VIEWS.SELECT_PLATFORM ? 'a platform' : 'an account'}`}
              onLogout={handleLogout}
            />
          </Panel>
          {renderRightColumn()}
        </Frame>
      </div>
    );
  }

  const isTightLogo = currentView !== VIEWS.LOGIN;

  return (
    <div className={classes.wrapper}>
      <Frame shape="single">
        <div
          className={twMerge(
            classes.logoRow,
            isTightLogo ? classes.logoTight : classes.logoWide
          )}>
          <Logo />
        </div>
        {renderAuthView()}
      </Frame>
    </div>
  );
}
